import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useNetworkStatus
 *
 * Production-grade hook that detects:
 *  - offline   : browser reports no connectivity
 *  - slow      : effective connection is 2g/slow-2g, or active latency probe exceeds threshold
 *  - restored  : connectivity returned after an offline/slow period (transient — auto-clears)
 *  - online    : default healthy state
 *
 * Detection strategy
 *  1. window 'online' / 'offline' events (instant, source of truth for hard offline)
 *  2. navigator.connection.effectiveType (when supported — 'slow-2g' | '2g' | '3g' | '4g')
 *  3. Active HEAD probe to a same-origin URL with AbortController + timeout. The probe
 *     is gated by document.visibilityState so background tabs don't burn CPU/network.
 *
 * Returned object
 *  { status, effectiveType, latencyMs, lastChangeAt, dismiss }
 *
 * Notes
 *  - 'restored' is intentionally transient so the UI can auto-dismiss it.
 *  - 'slow' classification is debounced (two consecutive slow signals required) to avoid
 *    flicker on a single bad probe.
 */

const SLOW_EFFECTIVE_TYPES = new Set(['slow-2g', '2g']);
const DEFAULT_PROBE_INTERVAL_MS = 30000;
const DEFAULT_PROBE_TIMEOUT_MS = 6000;
const DEFAULT_SLOW_LATENCY_MS = 2500;
const RESTORED_AUTO_CLEAR_MS = 4500;

const getConnection = () =>
    (typeof navigator !== 'undefined' &&
        (navigator.connection || navigator.mozConnection || navigator.webkitConnection)) ||
    null;

const isBrowserOnline = () =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false;

export const useNetworkStatus = (options = {}) => {
    const {
        probeUrl = `${process.env.PUBLIC_URL || ''}/favicon.ico`,
        probeIntervalMs = DEFAULT_PROBE_INTERVAL_MS,
        probeTimeoutMs = DEFAULT_PROBE_TIMEOUT_MS,
        slowLatencyMs = DEFAULT_SLOW_LATENCY_MS,
    } = options;

    const [state, setState] = useState(() => ({
        status: isBrowserOnline() ? 'online' : 'offline',
        effectiveType: getConnection()?.effectiveType || null,
        latencyMs: null,
        lastChangeAt: Date.now(),
    }));

    // refs avoid stale-closure problems in the probe loop
    const statusRef = useRef(state.status);
    statusRef.current = state.status;
    const slowStreakRef = useRef(0);
    const probeAbortRef = useRef(null);
    const probeTimerRef = useRef(null);
    const restoredClearTimerRef = useRef(null);
    const mountedRef = useRef(true);

    const transition = useCallback((next) => {
        if (!mountedRef.current) return;
        setState((prev) => {
            if (
                prev.status === next.status &&
                prev.effectiveType === next.effectiveType &&
                prev.latencyMs === next.latencyMs
            ) {
                return prev;
            }
            return { ...prev, ...next, lastChangeAt: Date.now() };
        });
    }, []);

    const dismiss = useCallback(() => {
        if (statusRef.current === 'restored') {
            transition({ status: 'online' });
        }
    }, [transition]);

    const runProbe = useCallback(async () => {
        if (!mountedRef.current) return;
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        if (!isBrowserOnline()) return;

        // cancel any in-flight probe
        if (probeAbortRef.current) {
            try { probeAbortRef.current.abort(); } catch { /* ignore */ }
        }
        const controller = new AbortController();
        probeAbortRef.current = controller;
        const timeoutId = setTimeout(() => {
            try { controller.abort(); } catch { /* ignore */ }
        }, probeTimeoutMs);

        const startedAt = performance.now();
        let latency = null;
        let probeFailed = false;

        try {
            const url = `${probeUrl}${probeUrl.includes('?') ? '&' : '?'}_nsp=${Date.now()}`;
            await fetch(url, {
                method: 'GET',
                cache: 'no-store',
                signal: controller.signal,
                credentials: 'omit',
                mode: 'no-cors',
            });
            latency = performance.now() - startedAt;
        } catch {
            probeFailed = true;
        } finally {
            clearTimeout(timeoutId);
            probeAbortRef.current = null;
        }

        if (!mountedRef.current) return;

        // hard offline takes precedence — let the offline event handler manage that
        if (!isBrowserOnline()) return;

        const effectiveType = getConnection()?.effectiveType || null;
        const effectiveTypeIsSlow = effectiveType && SLOW_EFFECTIVE_TYPES.has(effectiveType);
        const latencyIsSlow = latency != null && latency > slowLatencyMs;
        const looksSlow = probeFailed || effectiveTypeIsSlow || latencyIsSlow;

        if (looksSlow) {
            slowStreakRef.current += 1;
            // require two consecutive slow signals before showing — debounces transient blips
            if (slowStreakRef.current >= 2 && statusRef.current !== 'slow') {
                transition({ status: 'slow', effectiveType, latencyMs: latency });
            } else if (statusRef.current === 'slow') {
                transition({ status: 'slow', effectiveType, latencyMs: latency });
            }
        } else {
            slowStreakRef.current = 0;
            if (statusRef.current === 'slow') {
                transition({ status: 'restored', effectiveType, latencyMs: latency });
            } else {
                transition({ status: 'online', effectiveType, latencyMs: latency });
            }
        }
    }, [probeUrl, probeTimeoutMs, slowLatencyMs, transition]);

    // online / offline event wiring
    useEffect(() => {
        mountedRef.current = true;

        const handleOffline = () => {
            slowStreakRef.current = 0;
            transition({ status: 'offline' });
        };

        const handleOnline = () => {
            slowStreakRef.current = 0;
            transition({
                status: 'restored',
                effectiveType: getConnection()?.effectiveType || null,
            });
            // re-probe immediately to refine latency reading
            runProbe();
        };

        const handleConnectionChange = () => {
            const conn = getConnection();
            if (!conn) return;
            const effectiveType = conn.effectiveType || null;
            if (!isBrowserOnline()) return;
            if (effectiveType && SLOW_EFFECTIVE_TYPES.has(effectiveType)) {
                slowStreakRef.current = Math.max(slowStreakRef.current, 2);
                transition({ status: 'slow', effectiveType });
            } else if (statusRef.current === 'slow') {
                slowStreakRef.current = 0;
                transition({ status: 'restored', effectiveType });
            } else {
                transition({ effectiveType });
            }
        };

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') runProbe();
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        document.addEventListener('visibilitychange', handleVisibility);
        const conn = getConnection();
        if (conn && typeof conn.addEventListener === 'function') {
            conn.addEventListener('change', handleConnectionChange);
        }

        // start periodic probing
        probeTimerRef.current = setInterval(runProbe, probeIntervalMs);
        // initial probe (small delay so app finishes mounting)
        const kickoff = setTimeout(runProbe, 1500);

        return () => {
            mountedRef.current = false;
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            document.removeEventListener('visibilitychange', handleVisibility);
            if (conn && typeof conn.removeEventListener === 'function') {
                conn.removeEventListener('change', handleConnectionChange);
            }
            if (probeTimerRef.current) clearInterval(probeTimerRef.current);
            if (restoredClearTimerRef.current) clearTimeout(restoredClearTimerRef.current);
            clearTimeout(kickoff);
            if (probeAbortRef.current) {
                try { probeAbortRef.current.abort(); } catch { /* ignore */ }
            }
        };
    }, [probeIntervalMs, runProbe, transition]);

    // auto-clear 'restored' so the toast disappears after a few seconds
    useEffect(() => {
        if (state.status !== 'restored') return undefined;
        if (restoredClearTimerRef.current) clearTimeout(restoredClearTimerRef.current);
        restoredClearTimerRef.current = setTimeout(() => {
            if (!mountedRef.current) return;
            if (statusRef.current === 'restored') transition({ status: 'online' });
        }, RESTORED_AUTO_CLEAR_MS);
        return () => {
            if (restoredClearTimerRef.current) clearTimeout(restoredClearTimerRef.current);
        };
    }, [state.status, transition]);

    return { ...state, dismiss };
};

export default useNetworkStatus;
